import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import startOfWeek from 'date-fns/startOfWeek';
import min from 'date-fns/min';
import max from 'date-fns/max';
import omit from 'lodash.omit';
import pick from '../utils/pick';
import validInterval from '@oberoninternal/travelbase-ds/utils/validInterval';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import activitySearchParams from '../constants/activitySearchParams';
import { Weekday, Weekdays } from '../utils/weekday';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import format from 'date-fns/format';

import { ActivityHitFragment, ActivityPlannerTimeslotFragment } from '../generated/graphql';
import createImgProps from '../utils/createImgProps';
import getBestTimeslot from '../utils/getBestTimeslot';
import ActivityTile, { ActivityTileProps } from './ActivityTile';
import isSameDay from 'date-fns/isSameDay';
import { useQueryParams } from 'use-query-params';
import { isUpcoming } from './ActivityPlanner';
import parseDateTime from '../utils/parseDateTime';
import { useTenantContext } from '../context/TenantContext';
import FavoriteIconButton from './FavoriteIconButton';
import useGetPageSlugByLocale from '../hooks/useGetPageSlugByLocale';
import getActivityState from '../utils/getActivityTileState';

interface Props extends ActivityTileProps {
    hit: ActivityHitFragment;
    sizes?: string;
    dontPassDate?: boolean;
    cta?: boolean;
}

const hourMessages = defineMessages({
    hour: {
        defaultMessage: 'uur',
        id: 'activityHour',
    },
});

const getDateRanges = (timeSlots: ActivityPlannerTimeslotFragment[], locale: string) => {
    const ranges = [];
    const dates = timeSlots.map(timeSlot => parseDateTime(timeSlot.startDateTime));
    if (!dates.length) {
        return [];
    }
    const minDate = min(dates);
    const maxDate = max(dates);
    if (!validInterval({ start: minDate, end: maxDate })) {
        return [];
    }
    const datesOfInterval = eachDayOfInterval({ start: minDate, end: maxDate }).filter(day =>
        dates?.some(date => isSameDay(date, day))
    );

    const uniqueSortedDays = datesOfInterval.sort((a, b) => {
        const dateA = format(a, 'EEEE');
        const dateB = format(b, 'EEEE');
        return Weekdays.indexOf(dateA.toLowerCase() as Weekday) - Weekdays.indexOf(dateB.toLowerCase() as Weekday);
    });

    for (let i = 0; i < uniqueSortedDays.length; i += 1) {
        const startDay = uniqueSortedDays[i];
        for (let j = i; j < uniqueSortedDays.length; j += 1) {
            const currentDay = uniqueSortedDays[j];
            const nextDay = uniqueSortedDays[j + 1] ? uniqueSortedDays[j + 1] : currentDay;
            const currentDayOfWeek = differenceInCalendarDays(currentDay, startOfWeek(currentDay, { weekStartsOn: 1 }));
            const lastTimeSlotOfWeek = differenceInCalendarDays(
                uniqueSortedDays[uniqueSortedDays.length - 1],
                startOfWeek(currentDay, { weekStartsOn: 1 })
            );

            const nextDayOfWeek = differenceInCalendarDays(nextDay, startOfWeek(nextDay, { weekStartsOn: 1 }));
            if (nextDayOfWeek - currentDayOfWeek > 1 || currentDayOfWeek === lastTimeSlotOfWeek) {
                ranges.push({
                    start: startDay.toLocaleString(locale, { weekday: 'short' }),
                    end: currentDay.toLocaleString(locale, { weekday: 'short' }),
                });
                i = j;
                break;
            }
        }
    }
    const uniqueRanges: { start: string; end: string }[] = [...new Set(ranges.map(range => JSON.stringify(range)))].map(
        range => JSON.parse(range)
    );
    return uniqueRanges;
};
const ActivityHit = ({ hit, sizes, dontPassDate, cta = false, ...props }: Props) => {
    const { timeslots } = hit;
    const { brandConfig } = useTenantContext();
    const bestTimeslot = getBestTimeslot(timeslots, hit.bookingType);
    const [params] = useQueryParams(activitySearchParams);
    const intl = useIntl();
    const { getPageSlugByLocale } = useGetPageSlugByLocale();
    const activityPageSlug = getPageSlugByLocale('ACTIVITY');
    const router = useRouter();
    const { locale } = useIntl();
    const startDate = useMemo(() => params.startDate && new Date(params.startDate), [params.startDate]);
    const endDate = useMemo(() => params.endDate && new Date(params.endDate), [params.endDate]);
    const isSingleDay = !!(startDate && endDate && isSameDay(startDate, endDate));
    const isToday = isSingleDay && startDate && isSameDay(new Date(), startDate);
    const isFutureSingleDay = startDate && endDate && !isToday && isSameDay(startDate, endDate);
    const isRange = !isSingleDay;

    const ranges: { start: string; end: string }[] = useMemo(
        () => getDateRanges(timeslots, locale),
        [timeslots, locale]
    );
    const upcomingTimeslots = timeslots?.filter(timeslot => isUpcoming(timeslot));
    const hasTimeSlots = upcomingTimeslots.length > 0;

    const startTime = `${new Date(timeslots[0]?.startDateTime).getHours()}:${new Date(
        timeslots[0]?.startDateTime
    ).getMinutes()}`;
    const endTime = `${new Date(timeslots[0]?.endDateTime).getHours()}:${new Date(
        timeslots[0]?.endDateTime
    ).getMinutes()}`;
    const isFullDay = startTime === '0:0' && endTime === '23:55';

    const timeSlotChildren = useMemo(
        () => (
            <>
                {isFullDay && (isFutureSingleDay || isToday) && <FormattedMessage defaultMessage="Hele dag" />}
                {(isToday || (isFutureSingleDay && !isFullDay)) && hasTimeSlots && (
                    <strong>
                        {isToday && (
                            <>
                                <FormattedMessage defaultMessage="Vandaag, vanaf" />
                                &nbsp;
                            </>
                        )}
                        {[...upcomingTimeslots]
                            ?.sort(
                                (timeslotA, timeslotB) =>
                                    parseDateTime(timeslotA.startDateTime).valueOf() -
                                    parseDateTime(timeslotB.startDateTime).valueOf()
                            )

                            ?.map((timeSlot, index) => (
                                <React.Fragment key={timeSlot.id}>
                                    {format(parseDateTime(timeSlot.startDateTime), 'HH:mm')}{' '}
                                    {intl.formatMessage(hourMessages.hour)}
                                    {index !== upcomingTimeslots.length - 1 && <>&nbsp;&middot;&nbsp;</>}
                                </React.Fragment>
                            ))}
                    </strong>
                )}

                {isRange && hasTimeSlots && (
                    <strong>
                        {ranges.map((range, index) => (
                            <React.Fragment key={range.start}>
                                {range.start !== range.end ? `${range.start} - ${range.end}` : range.end}
                                {index !== ranges.length - 1 && <>&nbsp;&middot;&nbsp;</>}
                            </React.Fragment>
                        ))}
                    </strong>
                )}
            </>
        ),
        [hasTimeSlots, intl, isFutureSingleDay, isRange, isToday, ranges, upcomingTimeslots, isFullDay]
    );

    return (
        <ActivityTile
            cta={cta}
            ticketSalesEnabled={hit.bookingType === 'DIRECT'}
            rating={hit.activityReviewStats?.average ?? 0}
            reviewCount={hit.activityReviewStats?.count ?? 0}
            title={hit.name}
            videoUrl={hit.videoUrl}
            place={hit.place}
            timeSlotChildren={timeSlotChildren}
            themes={hit.themes.map(t => t.name).join(', ') ?? undefined}
            icon={brandConfig.enableFavorites && <FavoriteIconButton type="activity" id={hit.id} />}
            linkProps={{
                href: {
                    pathname: `/${activityPageSlug}/${hit.slug}`,
                    query: pick(
                        omit(router.query, ['view', ...(dontPassDate ? ['date'] : [])]),
                        Object.keys(activitySearchParams)
                    ),
                },
                locale: intl.locale,
            }}
            fullUrl={`${typeof window !== 'undefined' && window.location.origin}/${activityPageSlug}/${hit.slug}`}
            img={
                hit.listImage && hit.webpListImage ? createImgProps(hit.listImage, hit.webpListImage, sizes) : undefined
            }
            activityState={getActivityState(hit)}
            prices={bestTimeslot?.rateGroup?.rates.map(rate => ({
                name: rate.name,
                price: rate.price,
            }))}
            {...props}
        />
    );
};

export default ActivityHit;
